import { BehaviorSubject } from 'rxjs';
import { DISPLAY_LAYOUT, ROLE } from '../js/constants/constants';
import { getUser, getUsers, updateUser } from '../services/user.service';
import { DisplayLayout, User, UserSettings, UserUpdatePayload } from '../types/User';

export const allUsersList$ = new BehaviorSubject<User[]>([]);
export const isGuestUser$ = new BehaviorSubject(false);

export const getIsGuest = () => isGuestUser$.getValue(); // a synchronous snapshot

export const isUserAdmin = (roles: string[]): boolean => {
    return roles.includes(ROLE.ORG_ADMIN) || roles.includes(ROLE.PLATFORM_ADMIN);
};

export const promoteUserToAdmin = async (user: User) => {
    const body: UserUpdatePayload = {
        userName: user.userName,
        roles: [ROLE.ORG_ADMIN]
    };

    // get the user so we can get an updated etag
    await getUser(user.key);

    const response = await updateUser(body, user.key);

    return response;
};

export const demoteUserFromAdmin = async (user: User) => {
    const body: UserUpdatePayload = {
        userName: user.userName,
        roles: [ROLE.ORG_USER]
    };

    // get the user so we can get an updated etag
    await getUser(user.key);

    const response = await updateUser(body, user.key);

    return response;
};

export const getAllUsers = async () => {
    const { items } = await getUsers({ page: 1, perpage: 9999 });
    allUsersList$.next(items);
};

export const getAllNonAdminUsers = async () => {
    const { items } = await getUsers({ page: 1, perpage: 9999 });
    allUsersList$.next(removeAdminsFromUserList(items));
};

export const removeAdminsFromUserList = (users: User[]) =>
    users.filter((u) => !(u.roles.includes(ROLE.ORG_ADMIN) || u.roles.includes(ROLE.PLATFORM_ADMIN)));

export const getUserSettings = (key: string) => {
    const userSettings = localStorage.getItem(key);
    return userSettings ? JSON.parse(userSettings) : null;
};

export const setUserSettings = (key: string, userSettings: unknown) => {
    localStorage.setItem(key, JSON.stringify(userSettings));
};

export const setUserDisplaySettings = (userKey: string, layout: DisplayLayout) => {
    // set the user's display setting, merged so other display preferences (e.g. the viewer sidebar
    // width) survive - this runs on every files browser mount
    const settings: UserSettings | null = getUserSettings(userKey);

    setUserSettings(userKey, { ...settings, display: { ...settings?.display, layout } });
};

export const getUserDisplaySettings = (userKey: string): DisplayLayout => {
    const settings: UserSettings | null = getUserSettings(userKey);

    // default to grid if no settings
    return settings?.display?.layout ?? DISPLAY_LAYOUT.GRID;
};

/** The viewer sidebar width the user last dragged to, or null for the responsive default. */
export const getViewerSidebarWidth = (userKey: string): number | null => {
    if (!userKey) {
        return null;
    }

    const width = (getUserSettings(userKey) as UserSettings | null)?.display?.viewerSidebarWidth;
    return typeof width === 'number' && Number.isFinite(width) ? width : null;
};

/** Remembers the viewer sidebar width across models; null forgets it (back to the responsive default). */
export const setViewerSidebarWidth = (userKey: string, width: number | null) => {
    // The user subject starts as an empty user; writing then would land under the key "".
    if (!userKey) {
        return;
    }

    const settings: UserSettings | null = getUserSettings(userKey);
    const display: UserSettings['display'] = { ...settings?.display };

    if (width == null) {
        delete display.viewerSidebarWidth;
    } else {
        display.viewerSidebarWidth = Math.round(width);
    }

    setUserSettings(userKey, { ...settings, display });
};

export const hasUserAcceptedTerms = async (user: User) => {
    return user.hasAcceptedLatestTermsOfService ?? false;
};

